import { ToolCall } from '../../../../types/chat';
import { InsightType } from '../../../utils/designSystem';

export interface InsightMetric {
  label: string;
  value: string | number;
  format?: 'currency' | 'percentage' | 'number' | 'text';
  trend?: 'up' | 'down' | 'neutral';
}

export interface InsightAction {
  type: 'download';
  reportId: string;
  filename: string;
}

export interface TransformedInsight {
  type: InsightType;
  title: string;
  summary: string;
  metrics: InsightMetric[];
  rawData: unknown;
  icon?: string;
  action?: InsightAction;
}

// Format currency values
export function formatCurrency(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) {
    return 'N/A';
  }
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toLocaleString()}`;
}

// Format percentage values
export function formatPercentage(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) {
    return 'N/A';
  }
  return `${value.toFixed(1)}%`;
}

// Format number values
export function formatNumber(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) {
    return 'N/A';
  }
  return value.toLocaleString();
}

// Get display name for tools
function getToolDisplayName(toolName: string): string {
  const names: Record<string, string> = {
    query_hotel_data: 'Property Data Retrieved',
    run_comprehensive_analysis: 'Comprehensive Analysis',
    run_focused_analysis: 'Market Analysis',
    calculate_valuation: 'Property Valuation',
    get_analysis_history: 'Analysis History',
    calculate_financial_metrics: 'Financial Metrics',
    get_market_data: 'Market Data',
    run_risk_analysis: 'Risk Assessment',
    generate_report: 'Report Generated',
    search_competitor_rates: 'Competitor Rates',
    project_investment_returns: 'Investment Projections',
    manage_scenarios: 'Scenario Management',
    get_portfolio_analytics: 'Portfolio Analytics',
    calculate_debt_schedule: 'Debt Schedule',
  };
  return names[toolName] || toolName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

// Get insight type based on tool name
function getInsightType(toolName: string): InsightType {
  const typeMap: Record<string, InsightType> = {
    calculate_valuation: 'valuation',
    run_comprehensive_analysis: 'financial',
    run_focused_analysis: 'market',
    calculate_financial_metrics: 'financial',
    run_risk_analysis: 'risk',
    query_hotel_data: 'general',
    get_analysis_history: 'general',
    get_market_data: 'market',
    generate_report: 'general',
    search_competitor_rates: 'market',
    project_investment_returns: 'financial',
    manage_scenarios: 'general',
    get_portfolio_analytics: 'financial',
    calculate_debt_schedule: 'financial',
  };
  return typeMap[toolName] || 'general';
}

// Extract a safe value from nested object
function safeGet(obj: unknown, path: string, defaultValue: unknown = undefined): unknown {
  if (!obj || typeof obj !== 'object') return defaultValue;

  const keys = path.split('.');
  let current: unknown = obj;

  for (const key of keys) {
    if (current === null || current === undefined) return defaultValue;
    if (typeof current !== 'object') return defaultValue;
    current = (current as Record<string, unknown>)[key];
  }

  return current ?? defaultValue;
}

// Generate summary for valuation results
function generateValuationSummary(result: unknown): string {
  const value = safeGet(result, 'estimatedValue') || safeGet(result, 'value') || safeGet(result, 'valuation');
  const method = safeGet(result, 'method') || safeGet(result, 'approach') || 'income';

  if (typeof value === 'number') {
    return `Estimated property value is ${formatCurrency(value)} based on the ${method} approach.`;
  }
  return 'Property valuation analysis completed.';
}

// Generate summary for comprehensive analysis
function generateAnalysisSummary(result: unknown): string {
  const score = safeGet(result, 'score') || safeGet(result, 'overallScore');
  const recommendation = safeGet(result, 'recommendation');

  if (typeof score === 'number') {
    const rating = score >= 80 ? 'Strong' : score >= 60 ? 'Moderate' : 'Weak';
    return `Overall investment score: ${score}/100 - ${rating} investment potential.${recommendation ? ` ${recommendation}` : ''}`;
  }
  return 'Comprehensive analysis completed successfully.';
}

// Generate summary for market analysis
function generateMarketSummary(result: unknown): string {
  const rank = safeGet(result, 'marketRank') || safeGet(result, 'percentile');
  const trend = safeGet(result, 'marketTrend') || safeGet(result, 'trend');

  if (rank) {
    return `Property ranks in the ${rank}${typeof rank === 'number' ? 'th percentile' : ''} of comparable properties.${trend ? ` Market trend: ${trend}.` : ''}`;
  }
  return 'Market analysis completed. Review metrics below for details.';
}

// Generate summary for risk analysis
function generateRiskSummary(result: unknown): string {
  const level = safeGet(result, 'riskLevel') || safeGet(result, 'overallRisk');
  const factors = safeGet(result, 'riskFactors') as unknown[] | undefined;

  if (level) {
    const factorCount = Array.isArray(factors) ? factors.length : 0;
    return `Risk level: ${level}.${factorCount > 0 ? ` ${factorCount} risk factor${factorCount > 1 ? 's' : ''} identified.` : ''}`;
  }
  return 'Risk assessment completed.';
}

// Generate summary for general queries
function generateGeneralSummary(result: unknown): string {
  if (Array.isArray(result)) {
    return `Retrieved ${result.length} data point${result.length !== 1 ? 's' : ''}.`;
  }
  if (result && typeof result === 'object') {
    const keys = Object.keys(result);
    return `Retrieved ${keys.length} data field${keys.length !== 1 ? 's' : ''}.`;
  }
  return 'Data query completed successfully.';
}

// Extract metrics from valuation result
function extractValuationMetrics(result: unknown): InsightMetric[] {
  const metrics: InsightMetric[] = [];

  const value = safeGet(result, 'estimatedValue') || safeGet(result, 'value');
  if (typeof value === 'number') {
    metrics.push({ label: 'Estimated Value', value: formatCurrency(value), format: 'currency' });
  }

  const capRate = safeGet(result, 'capRate') || safeGet(result, 'capitalizationRate');
  if (typeof capRate === 'number') {
    metrics.push({ label: 'Cap Rate', value: formatPercentage(capRate), format: 'percentage' });
  }

  const noi = safeGet(result, 'noi') || safeGet(result, 'netOperatingIncome');
  if (typeof noi === 'number') {
    metrics.push({ label: 'NOI', value: formatCurrency(noi), format: 'currency' });
  }

  const pricePerRoom = safeGet(result, 'pricePerRoom') || safeGet(result, 'valuePerKey');
  if (typeof pricePerRoom === 'number') {
    metrics.push({ label: 'Price/Room', value: formatCurrency(pricePerRoom), format: 'currency' });
  }

  return metrics;
}

// Extract metrics from financial analysis result
function extractFinancialMetrics(result: unknown): InsightMetric[] {
  const metrics: InsightMetric[] = [];

  const score = safeGet(result, 'score') || safeGet(result, 'overallScore');
  if (typeof score === 'number') {
    metrics.push({ label: 'Overall Score', value: `${score}/100`, format: 'text' });
  }

  const revenue = safeGet(result, 'totalRevenue') || safeGet(result, 'revenue');
  if (typeof revenue === 'number') {
    metrics.push({ label: 'Total Revenue', value: formatCurrency(revenue), format: 'currency' });
  }

  const gop = safeGet(result, 'gop') || safeGet(result, 'grossOperatingProfit');
  if (typeof gop === 'number') {
    metrics.push({ label: 'GOP', value: formatCurrency(gop), format: 'currency' });
  }

  const revpar = safeGet(result, 'revpar') || safeGet(result, 'revPAR');
  if (typeof revpar === 'number') {
    metrics.push({ label: 'RevPAR', value: formatCurrency(revpar), format: 'currency' });
  }

  const occupancy = safeGet(result, 'occupancy') || safeGet(result, 'occupancyRate');
  if (typeof occupancy === 'number') {
    metrics.push({ label: 'Occupancy', value: formatPercentage(occupancy), format: 'percentage' });
  }

  return metrics;
}

// Extract metrics from market analysis result
function extractMarketMetrics(result: unknown): InsightMetric[] {
  const metrics: InsightMetric[] = [];

  const rank = safeGet(result, 'marketRank') || safeGet(result, 'percentile');
  if (rank !== undefined) {
    metrics.push({ label: 'Market Rank', value: typeof rank === 'number' ? `${rank}th percentile` : String(rank), format: 'text' });
  }

  const trend = safeGet(result, 'marketTrend') || safeGet(result, 'trend');
  if (trend) {
    metrics.push({ label: 'Market Trend', value: String(trend), format: 'text' });
  }

  const avgADR = safeGet(result, 'marketADR') || safeGet(result, 'avgADR');
  if (typeof avgADR === 'number') {
    metrics.push({ label: 'Market ADR', value: formatCurrency(avgADR), format: 'currency' });
  }

  const avgOccupancy = safeGet(result, 'marketOccupancy') || safeGet(result, 'avgOccupancy');
  if (typeof avgOccupancy === 'number') {
    metrics.push({ label: 'Market Occupancy', value: formatPercentage(avgOccupancy), format: 'percentage' });
  }

  return metrics;
}

// Extract metrics from risk analysis result
function extractRiskMetrics(result: unknown): InsightMetric[] {
  const metrics: InsightMetric[] = [];

  const level = safeGet(result, 'riskLevel') || safeGet(result, 'overallRisk');
  if (level) {
    metrics.push({ label: 'Risk Level', value: String(level), format: 'text' });
  }

  const factors = safeGet(result, 'riskFactors') as unknown[] | undefined;
  if (Array.isArray(factors) && factors.length > 0) {
    metrics.push({ label: 'Risk Factors', value: factors.length, format: 'number' });
  }

  const mitigations = safeGet(result, 'mitigations') as unknown[] | undefined;
  if (Array.isArray(mitigations) && mitigations.length > 0) {
    metrics.push({ label: 'Mitigations', value: mitigations.length, format: 'number' });
  }

  return metrics;
}

// Extract general metrics from unknown result
function extractGeneralMetrics(result: unknown): InsightMetric[] {
  const metrics: InsightMetric[] = [];

  if (!result || typeof result !== 'object') {
    return metrics;
  }

  // Try to extract common fields
  const obj = result as Record<string, unknown>;
  const commonFields = ['name', 'city', 'state', 'roomCount', 'room_count', 'propertyType', 'property_type'];

  for (const field of commonFields) {
    if (obj[field] !== undefined && obj[field] !== null) {
      const label = field.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
      const capitalizedLabel = label.charAt(0).toUpperCase() + label.slice(1);
      metrics.push({ label: capitalizedLabel, value: String(obj[field]), format: 'text' });
    }
    if (metrics.length >= 4) break; // Limit to 4 metrics
  }

  return metrics;
}

// Generate summary for competitor rates
function generateCompetitorRatesSummary(result: unknown): string {
  const competitors = safeGet(result, 'competitors') as unknown[] | undefined;
  const avgRate = safeGet(result, 'averageRate') || safeGet(result, 'avgRate');
  if (Array.isArray(competitors) && competitors.length > 0) {
    return `Found ${competitors.length} competitor${competitors.length > 1 ? 's' : ''} in the area.${typeof avgRate === 'number' ? ` Average nightly rate: ${formatCurrency(avgRate)}.` : ''}`;
  }
  if (typeof avgRate === 'number') {
    return `Average competitor rate: ${formatCurrency(avgRate)}.`;
  }
  return 'Competitor rate search completed.';
}

// Extract metrics from competitor rates result
function extractCompetitorRatesMetrics(result: unknown): InsightMetric[] {
  const metrics: InsightMetric[] = [];
  const avgRate = safeGet(result, 'averageRate') || safeGet(result, 'avgRate');
  if (typeof avgRate === 'number') {
    metrics.push({ label: 'Avg Rate', value: formatCurrency(avgRate), format: 'currency' });
  }
  const minRate = safeGet(result, 'minRate') || safeGet(result, 'lowestRate');
  if (typeof minRate === 'number') {
    metrics.push({ label: 'Lowest Rate', value: formatCurrency(minRate), format: 'currency' });
  }
  const maxRate = safeGet(result, 'maxRate') || safeGet(result, 'highestRate');
  if (typeof maxRate === 'number') {
    metrics.push({ label: 'Highest Rate', value: formatCurrency(maxRate), format: 'currency' });
  }
  const competitors = safeGet(result, 'competitors') as unknown[] | undefined;
  if (Array.isArray(competitors)) {
    metrics.push({ label: 'Hotels Found', value: competitors.length, format: 'number' });
  }
  return metrics;
}

// Generate summary for investment returns
function generateInvestmentReturnsSummary(result: unknown): string {
  const irr = safeGet(result, 'returns.irr') || safeGet(result, 'irr');
  const equityMultiple = safeGet(result, 'returns.equityMultiple') || safeGet(result, 'equityMultiple');
  if (typeof irr === 'number') {
    return `Projected IRR: ${formatPercentage(irr)}${typeof equityMultiple === 'number' ? `, Equity Multiple: ${equityMultiple.toFixed(2)}x` : ''}.`;
  }
  return 'Investment return projections completed.';
}

// Extract metrics from investment returns result
function extractInvestmentReturnsMetrics(result: unknown): InsightMetric[] {
  const metrics: InsightMetric[] = [];
  const returns = (safeGet(result, 'returns') || result) as Record<string, unknown> | undefined;
  if (!returns) return metrics;

  const irr = safeGet(returns, 'irr');
  if (typeof irr === 'number') {
    metrics.push({ label: 'IRR', value: formatPercentage(irr), format: 'percentage' });
  }
  const equityMultiple = safeGet(returns, 'equityMultiple') || safeGet(returns, 'equity_multiple');
  if (typeof equityMultiple === 'number') {
    metrics.push({ label: 'Equity Multiple', value: `${equityMultiple.toFixed(2)}x`, format: 'text' });
  }
  const cashOnCash = safeGet(returns, 'cashOnCash') || safeGet(returns, 'cash_on_cash');
  if (typeof cashOnCash === 'number') {
    metrics.push({ label: 'Cash-on-Cash', value: formatPercentage(cashOnCash), format: 'percentage' });
  }
  const exitValue = safeGet(result, 'exitValue');
  if (typeof exitValue === 'number') {
    metrics.push({ label: 'Exit Value', value: formatCurrency(exitValue), format: 'currency' });
  }
  return metrics;
}

// Generate summary for portfolio analytics
function generatePortfolioSummary(result: unknown): string {
  const health = safeGet(result, 'health.overallScore') || safeGet(result, 'health.score');
  const summary = safeGet(result, 'summary');
  if (typeof health === 'number') {
    return `Portfolio health score: ${health}/100.`;
  }
  if (summary && typeof summary === 'object') {
    const totalHotels = safeGet(summary, 'totalHotels') || safeGet(summary, 'total_hotels');
    if (typeof totalHotels === 'number') {
      return `Portfolio overview: ${totalHotels} propert${totalHotels === 1 ? 'y' : 'ies'}.`;
    }
  }
  return 'Portfolio analytics retrieved.';
}

// Extract metrics from portfolio analytics result
function extractPortfolioMetrics(result: unknown): InsightMetric[] {
  const metrics: InsightMetric[] = [];
  const health = safeGet(result, 'health');
  if (health && typeof health === 'object') {
    const score = safeGet(health, 'overallScore') || safeGet(health, 'score');
    if (typeof score === 'number') {
      metrics.push({ label: 'Health Score', value: `${score}/100`, format: 'text' });
    }
  }
  const summary = safeGet(result, 'summary');
  if (summary && typeof summary === 'object') {
    const totalHotels = safeGet(summary, 'totalHotels') || safeGet(summary, 'total_hotels');
    if (typeof totalHotels === 'number') {
      metrics.push({ label: 'Properties', value: totalHotels, format: 'number' });
    }
    const totalValue = safeGet(summary, 'totalValue') || safeGet(summary, 'total_value');
    if (typeof totalValue === 'number') {
      metrics.push({ label: 'Portfolio Value', value: formatCurrency(totalValue), format: 'currency' });
    }
  }
  const revenue = safeGet(result, 'revenue');
  if (revenue && typeof revenue === 'object') {
    const avgRevPAR = safeGet(revenue, 'avgRevPAR') || safeGet(revenue, 'avg_revpar');
    if (typeof avgRevPAR === 'number') {
      metrics.push({ label: 'Avg RevPAR', value: formatCurrency(avgRevPAR), format: 'currency' });
    }
  }
  return metrics;
}

// Generate summary for debt schedule
function generateDebtSummary(result: unknown): string {
  const summary = safeGet(result, 'summary');
  if (summary && typeof summary === 'object') {
    const monthly = safeGet(summary, 'monthlyPayment');
    if (typeof monthly === 'number') {
      return `Monthly payment: ${formatCurrency(monthly)}.`;
    }
  }
  const blendedRate = safeGet(result, 'blendedRate') || safeGet(result, 'blended_rate');
  if (typeof blendedRate === 'number') {
    return `Blended interest rate: ${formatPercentage(blendedRate)}.`;
  }
  const annualDebtService = safeGet(result, 'annualDebtService');
  if (typeof annualDebtService === 'number') {
    return `Annual debt service: ${formatCurrency(annualDebtService)}.`;
  }
  return 'Debt schedule calculated.';
}

// Extract metrics from debt schedule result
function extractDebtMetrics(result: unknown): InsightMetric[] {
  const metrics: InsightMetric[] = [];
  const summary = safeGet(result, 'summary');
  if (summary && typeof summary === 'object') {
    const monthly = safeGet(summary, 'monthlyPayment');
    if (typeof monthly === 'number') {
      metrics.push({ label: 'Monthly Payment', value: formatCurrency(monthly), format: 'currency' });
    }
    const annual = safeGet(summary, 'annualPayment');
    if (typeof annual === 'number') {
      metrics.push({ label: 'Annual Payment', value: formatCurrency(annual), format: 'currency' });
    }
    const totalInterest = safeGet(summary, 'totalInterest');
    if (typeof totalInterest === 'number') {
      metrics.push({ label: 'Total Interest', value: formatCurrency(totalInterest), format: 'currency' });
    }
    const rate = safeGet(summary, 'interestRate');
    if (typeof rate === 'number') {
      metrics.push({ label: 'Interest Rate', value: formatPercentage(rate), format: 'percentage' });
    }
  }
  const blendedRate = safeGet(result, 'blendedRate') || safeGet(result, 'blended_rate');
  if (typeof blendedRate === 'number') {
    metrics.push({ label: 'Blended Rate', value: formatPercentage(blendedRate), format: 'percentage' });
  }
  const annualDebtService = safeGet(result, 'annualDebtService');
  if (typeof annualDebtService === 'number') {
    metrics.push({ label: 'Annual Debt Service', value: formatCurrency(annualDebtService), format: 'currency' });
  }
  return metrics;
}

// Generate summary for report generation
function generateReportSummary(result: unknown): string {
  const success = safeGet(result, 'success');
  const reportType = safeGet(result, 'reportType') || safeGet(result, 'type');
  if (success) {
    return `${reportType ? String(reportType).replace(/_/g, ' ') + ' r' : 'R'}eport generated successfully.`;
  }
  const error = safeGet(result, 'error');
  if (error) return `Report generation failed: ${error}`;
  return 'Report generation completed.';
}

// Extract report metrics (format, size)
function extractReportMetrics(result: unknown): InsightMetric[] {
  const metrics: InsightMetric[] = [];
  const outputFormat = safeGet(result, 'outputFormat');
  if (outputFormat) {
    metrics.push({ label: 'Format', value: String(outputFormat).toUpperCase(), format: 'text' });
  }
  const fileSize = safeGet(result, 'fileSize');
  if (typeof fileSize === 'number') {
    const sizeStr = fileSize >= 1048576
      ? `${(fileSize / 1048576).toFixed(1)} MB`
      : `${(fileSize / 1024).toFixed(0)} KB`;
    metrics.push({ label: 'File Size', value: sizeStr, format: 'text' });
  }
  return metrics;
}

// Extract download action from report result
function extractReportAction(result: unknown): InsightAction | undefined {
  const success = safeGet(result, 'success');
  const reportId = safeGet(result, 'reportId');
  const filename = safeGet(result, 'filename');
  if (success && reportId && filename) {
    return { type: 'download', reportId: String(reportId), filename: String(filename) };
  }
  return undefined;
}

// Generate summary for scenario management
function generateScenarioSummary(result: unknown): string {
  if (Array.isArray(result)) {
    return `Found ${result.length} scenario${result.length !== 1 ? 's' : ''}.`;
  }
  const name = safeGet(result, 'name');
  if (name) {
    return `Scenario: ${name}.`;
  }
  return 'Scenario operation completed.';
}

// Main transformer function
export function transformToolResult(toolCall: ToolCall): TransformedInsight {
  const type = getInsightType(toolCall.name);
  const title = getToolDisplayName(toolCall.name);
  const result = toolCall.result;

  let summary: string;
  let metrics: InsightMetric[];
  let action: InsightAction | undefined;

  // Dispatch by tool name first for tool-specific extractors, fall back to type
  switch (toolCall.name) {
    case 'search_competitor_rates':
      summary = generateCompetitorRatesSummary(result);
      metrics = extractCompetitorRatesMetrics(result);
      break;
    case 'project_investment_returns':
      summary = generateInvestmentReturnsSummary(result);
      metrics = extractInvestmentReturnsMetrics(result);
      break;
    case 'get_portfolio_analytics':
      summary = generatePortfolioSummary(result);
      metrics = extractPortfolioMetrics(result);
      break;
    case 'calculate_debt_schedule':
      summary = generateDebtSummary(result);
      metrics = extractDebtMetrics(result);
      break;
    case 'generate_report':
      summary = generateReportSummary(result);
      metrics = extractReportMetrics(result);
      action = extractReportAction(result);
      break;
    case 'manage_scenarios':
      summary = generateScenarioSummary(result);
      metrics = extractGeneralMetrics(result);
      break;
    default:
      // Fall back to type-based dispatch for original tools
      switch (type) {
        case 'valuation':
          summary = generateValuationSummary(result);
          metrics = extractValuationMetrics(result);
          break;
        case 'financial':
          summary = generateAnalysisSummary(result);
          metrics = extractFinancialMetrics(result);
          break;
        case 'market':
          summary = generateMarketSummary(result);
          metrics = extractMarketMetrics(result);
          break;
        case 'risk':
          summary = generateRiskSummary(result);
          metrics = extractRiskMetrics(result);
          break;
        default:
          summary = generateGeneralSummary(result);
          metrics = extractGeneralMetrics(result);
      }
  }

  // Ensure we have at least some basic info
  if (metrics.length === 0 && result) {
    if (typeof result === 'object' && 'success' in (result as object)) {
      const success = (result as Record<string, unknown>).success;
      metrics.push({ label: 'Status', value: success ? 'Success' : 'Failed', format: 'text' });
    }
  }

  return {
    type,
    title,
    summary,
    metrics,
    rawData: result,
    action,
  };
}

// Transform multiple tool calls
export function transformToolResults(toolCalls: ToolCall[]): TransformedInsight[] {
  return toolCalls.map(transformToolResult);
}

export default transformToolResult;
